import React, {useMemo} from 'react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'

import {
    Box,
    Stack,
    Text,
    Accordion,
    AccordionButton,
    AccordionItem,
    AccordionPanel,
    Checkbox,
    Button,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    DrawerHeader,
    DrawerBody
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {CheckIcon, MinusIcon, PlusIcon} from '../../components/custom-icons'
import {createFilterCategories} from '@app/utils/store-utils'

/**
 * CustomCheckboxIcon component renders a custom icon for checkboxes.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.isChecked - Indicates if the checkbox is checked.
 * @param {boolean} props.isIndeterminate - Indicates if the checkbox is in an indeterminate state.
 * @returns {JSX.Element} The rendered component.
 */

const CustomCheckboxIcon = ({isChecked, isIndeterminate, ...rest}) => {
    return <>{isChecked ? <CheckIcon boxSize={'10px'} {...rest} /> : null}</>
}

CustomCheckboxIcon.propTypes = {
    isChecked: PropTypes.bool,
    isIndeterminate: PropTypes.bool
}

/**
 * StoresFilters component renders a filter panel for filtering stores based on collections, store types, and services.
 * It provides checkboxes for each filter category and displays the number of stores matching the selected filters.
 *
 * @component
 *
 * @param {Object} props - The component props.
 * @param {Function} props.handleCheckboxChange - Callback function to handle changes in the filter checkboxes.
 * @param {Function} props.toggleFilters - Callback function to toggle the visibility of the filter panel.
 * @param {Object} props.filters - An object representing the current state of filters applied.
 * @param {boolean} props.filters.men - Indicates if the "Men" collection filter is applied.
 * @param {boolean} props.filters.women - Indicates if the "Women" collection filter is applied.
 * @param {boolean} props.filters.kids - Indicates if the "Kids" collection filter is applied.
 * @param {boolean} props.filters.VS - Indicates if the "Vilebrequin stores" type filter is applied.
 * @param {boolean} props.filters.VO - Indicates if the "Outlet stores" type filter is applied.
 * @param {boolean} props.filters.OS - Indicates if the "Others stores" type filter is applied.
 * @param {boolean} props.filters.monogramming - Indicates if the "In Store Monogramming" service filter is applied.
 * @param {number} props.storesNumber - The number of stores matching the current filters.
 * @param {Function} props.clearFilters - Callback function to clear all applied filters.
 *
 * @returns {JSX.Element} The rendered component.
 */
const StoresFilters = ({
    handleCheckboxChange,
    onFiltersClose,
    isFiltersOpen,
    filters,
    storesNumber,
    clearFilters
}) => {
    const {formatMessage} = useIntl()
    const filterCategories = useMemo(() => createFilterCategories(formatMessage), [formatMessage])
    return (
        <Drawer isOpen={isFiltersOpen} placement="right" onClose={onFiltersClose} size={'full'}>
            <DrawerOverlay />
            <DrawerContent maxWidth={{base: 'unset', lg: '483px'}}>
                <DrawerCloseButton />
                <DrawerHeader>
                    <Text
                        as={'h3'}
                        variant={'bodyLarge'}
                        fontSize={'14px'}
                        lineHeight={1}
                        fontWeight={600}
                    >
                        {formatMessage({
                            defaultMessage: 'Filter',
                            id: 'stores_filters.title.filter'
                        })}
                    </Text>{' '}
                </DrawerHeader>
                <DrawerBody
                    display={'flex'}
                    flexDirection={'column'}
                    justifyContent={'space-between'}
                >
                    <Box>
                        {filterCategories.map((category) => (
                            <Accordion allowToggle={true} key={category.id}>
                                <AccordionItem border="none">
                                    {({isExpanded}) => (
                                        <>
                                            <AccordionButton
                                                flexDirection={'row'}
                                                justifyContent={'space-between'}
                                            >
                                                <Text>{category.label}</Text>
                                                {isExpanded ? (
                                                    <MinusIcon width={'12px'} height={'12px'} />
                                                ) : (
                                                    <PlusIcon width={'12px'} height={'12px'} />
                                                )}
                                            </AccordionButton>
                                            <AccordionPanel>
                                                {category.options.map((option) => (
                                                    <Checkbox
                                                        key={option.name}
                                                        icon={<CustomCheckboxIcon />}
                                                        iconSize="0.1rem"
                                                        name={option.name}
                                                        isChecked={filters[option.name]}
                                                        onChange={handleCheckboxChange}
                                                        marginBottom={'10px'}
                                                    >
                                                        {option.label}
                                                    </Checkbox>
                                                ))}
                                            </AccordionPanel>
                                        </>
                                    )}
                                </AccordionItem>
                            </Accordion>
                        ))}
                    </Box>
                    <Box>
                        {!storesNumber && (
                            <Text variant={'bodyRegular'} color={'red'} fontSize={'13px'}>
                                {formatMessage({
                                    id: 'stores_filters.error',
                                    defaultMessage:
                                        'Please modify your filter, no store for your research'
                                })}
                            </Text>
                        )}
                        <Stack gap={'10px'} direction={'row'}>
                            <Button
                                onClick={() => {
                                    clearFilters()
                                    onFiltersClose()
                                }}
                                variant={'secondary'}
                                flex={'0 0 50%'}
                            >
                                {formatMessage({
                                    id: 'stores_filters.clear',
                                    defaultMessage: 'Clear all'
                                })}
                            </Button>
                            <Button onClick={onFiltersClose} variant={'primary'} flex={'0 0 50%'}>
                                {formatMessage({
                                    id: 'stores_filters.view',
                                    defaultMessage: 'view all'
                                })}{' '}
                                {`(${storesNumber} stores)`}
                            </Button>
                        </Stack>
                    </Box>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    )
}

StoresFilters.propTypes = {
    handleCheckboxChange: PropTypes.func,
    filters: PropTypes.object,
    storesNumber: PropTypes.any,
    clearFilters: PropTypes.func,
    onFiltersClose: PropTypes.func,
    isFiltersOpen: PropTypes.bool
}

export default StoresFilters
